import React, { useEffect, useState, useCallback, useRef } from 'react';
import {
    Layout,
    Button,
    message,
    Input,
    Spin,
    Checkbox,
    Dropdown,
    Space,
    Modal,
    Upload,
    Typography,
} from 'antd';
import {
    InboxOutlined,
    MailOutlined,
    FileTextOutlined,
    DownOutlined,
    CalendarOutlined,
    SearchOutlined,
    LockOutlined,
    EyeOutlined,
    DownloadOutlined,
    FolderOpenOutlined,
    ClockCircleOutlined,
    CommentOutlined,
    FileAddOutlined,
    SendOutlined,
    EditOutlined,
    CheckCircleOutlined,
    CloseCircleOutlined,
    StopOutlined,
    UploadOutlined,
    DeleteOutlined,
    CheckOutlined,
} from '@ant-design/icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSignature } from '@fortawesome/free-solid-svg-icons';
import { useTranslation } from 'react-i18next';
import axios from '../../utility/axios';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import '../../styles/profile/ContactsMailbox.css';
import '../../styles/profile/Mailbox.css';
import '../../styles/documents/DocumentRequestRecords.css';
import '../../styles/documenso/DocumensoPreview.css';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { useContactsMailbox } from '../../hooks/useContactsMailbox';
import { useBecomeGuest } from '../../contexts/BecomeGuestContext';
import { isConvertible } from '../../utility/fileTypes';
import AttachmentPreview from '../../contexts/AttachmentPreview';
import DocumensoSignModal from '../documenso/DocumensoSignModal';

dayjs.extend(relativeTime);

const { Content } = Layout;
const { Text } = Typography;

const ContactsMailbox = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams] = useSearchParams();
    const rid = searchParams.get('rid');

    const {
        inboxData,
        loading,
        dataLoaded,
        requiresVerification,
        receiverEmail,
        isFreeAccount,
        refresh,
        fetchInbox,
    } = useContactsMailbox(rid);
    const { setCanBecomeGuest, setIsFreeAccount } = useBecomeGuest();

    const [accessCode, setAccessCode] = useState('');
    const [showAccessCodeInput, setShowAccessCodeInput] = useState(false);
    const [verifying, setVerifying] = useState(false);
    const [codeExpired, setCodeExpired] = useState(false);
    const [resending, setResending] = useState(false);
    const [resendCooldown, setResendCooldown] = useState(0);
    const [sendError, setSendError] = useState('');
    const [verifyError, setVerifyError] = useState('');
    const codeInputRef = useRef(null);
    const [resendingRecordRid, setResendingRecordRid] = useState(null);
    const [lockedRecordResendCooldown, setLockedRecordResendCooldown] = useState(0);
    const [lockedRecordAccessCode, setLockedRecordAccessCode] = useState('');
    const [lockedRecordVerifyError, setLockedRecordVerifyError] = useState('');
    const [verifyingLockedRecord, setVerifyingLockedRecord] = useState(false);
    const [sectionView, setSectionView] = useState('all');
    const [searchText, setSearchText] = useState('');
    const [sortConfig, setSortConfig] = useState({ key: 'created', direction: 'descend' });
    const [previewMessage, setPreviewMessage] = useState(null);
    const [previewThread, setPreviewThread] = useState(null);
    const [attachmentPreviewFile, setAttachmentPreviewFile] = useState(null);
    const [previewDocumentUploadedFiles, setPreviewDocumentUploadedFiles] = useState({});
    const [previewSystemSettings, setPreviewSystemSettings] = useState(null);
    const [previewDenyModalVisible, setPreviewDenyModalVisible] = useState(false);
    const [previewDenyReason, setPreviewDenyReason] = useState('');
    const [previewDenyingFileId, setPreviewDenyingFileId] = useState(null);
    const [documensoSignModalOpen, setDocumensoSignModalOpen] = useState(false);
    const [listWidth, setListWidth] = useState(() => parseFloat(localStorage.getItem('contacts_mailbox_list_width')) || 55);
    const [isResizingPanels, setIsResizingPanels] = useState(false);
    const containerRef = useRef(null);
    const [previewColumnWidths, setPreviewColumnWidths] = useState(() => {
        const saved = localStorage.getItem('contacts_mailbox_preview_column_widths');
        return saved ? JSON.parse(saved) : { body: 60, documents: 40 };
    });
    const [isResizingPreview, setIsResizingPreview] = useState(false);
    const [threadWidthPercent, setThreadWidthPercent] = useState(() => {
        const saved = localStorage.getItem('contacts_mailbox_thread_width_percent');
        return saved ? parseFloat(saved) : 25;
    });
    const [isResizingThread, setIsResizingThread] = useState(false);
    const previewContentRef = useRef(null);
    const allFolderData = {
        inbox: inboxData.threaded_messages || [],
        inbox_documents: inboxData.documents || [],
    };

    const excludeDocumenso = (list) => (list || []).filter((item) => item.message_type !== 'documenso');
    const countSignatureRequests = (list) => (list || []).filter((item) => item.message_type === 'documenso').length;

    const getCombinedEmails = useCallback(() => {
        if (!dataLoaded) return [];
        const messageData = allFolderData.inbox || [];
        const documentData = allFolderData.inbox_documents || [];
        let data = [];
        if (sectionView === 'all') {
            data = [...messageData, ...documentData];
        } else if (sectionView === 'messages') {
            data = messageData.filter((item) => item.message_type !== 'documenso');
        } else if (sectionView === 'documents') {
            data = documentData;
        } else if (sectionView === 'signature_requests') {
            data = messageData.filter((item) => item.message_type === 'documenso');
        }
        return data;
    }, [dataLoaded, sectionView, allFolderData.inbox, allFolderData.inbox_documents]);

    const emails = getCombinedEmails();

    const stripHtmlTags = useCallback((html) => {
        if (!html) return '';
        const tmp = document.createElement('DIV');
        tmp.innerHTML = html;
        return tmp.textContent || tmp.innerText || '';
    }, []);

    const filteredEmails = React.useMemo(() => {
        if (searchText.trim() === '') return emails;
        const term = searchText.toLowerCase();
        return emails.filter((item) =>
            (item.sender && item.sender.toLowerCase().includes(term)) ||
            (item.subject && item.subject.toLowerCase().includes(term)) ||
            (item.body && item.body?.toLowerCase().includes(term)) ||
            (item.preview && item.preview?.toLowerCase().includes(term)) ||
            (item.created_at && item.created_at.toLowerCase?.().includes(term))
        );
    }, [emails, searchText]);

    const sortData = useCallback(
        (data) => {
            if (!sortConfig.key || !sortConfig.direction) return data;
            return [...data].sort((a, b) => {
                let aVal, bVal;
                switch (sortConfig.key) {
                    case 'subject':
                        aVal = a.subject || '';
                        bVal = b.subject || '';
                        break;
                    case 'created':
                        aVal = new Date(a.created_at || 0);
                        bVal = new Date(b.created_at || 0);
                        break;
                    case 'expires':
                        aVal = new Date(a.ttlive || 0);
                        bVal = new Date(b.ttlive || 0);
                        break;
                    case 'read':
                        aVal = a.has_been_read ? 1 : 0;
                        bVal = b.has_been_read ? 1 : 0;
                        break;
                    default:
                        aVal = a[sortConfig.key] || '';
                        bVal = b[sortConfig.key] || '';
                }
                if (aVal instanceof Date && bVal instanceof Date) {
                    return sortConfig.direction === 'ascend' ? aVal - bVal : bVal - aVal;
                }
                if (typeof aVal === 'number' && typeof bVal === 'number') {
                    return sortConfig.direction === 'ascend' ? aVal - bVal : bVal - aVal;
                }
                const aStr = String(aVal).toLowerCase();
                const bStr = String(bVal).toLowerCase();
                return sortConfig.direction === 'ascend' ? aStr.localeCompare(bStr) : bStr.localeCompare(aStr);
            });
        },
        [sortConfig]
    );

    const hasReplies = (thread) => thread?.replies && Array.isArray(thread.replies) && thread.replies.length > 0;

    useEffect(() => {
        if (loading || !dataLoaded || filteredEmails.length === 0) return;
        const sorted = sortData(filteredEmails);
        const first = sorted[0];
        if (!first) return;
        const previewInList =
            previewMessage &&
            sorted.some((item) => {
                if (item.unique_identifier === previewMessage.unique_identifier) return true;
                if (hasReplies(item) && item.replies?.some((r) => r.unique_identifier === previewMessage.unique_identifier))
                    return true;
                return false;
            });
        if (!previewMessage || !previewInList) {
            setPreviewMessage(first);
            setPreviewThread(hasReplies(first) ? first : null);
        }
    }, [loading, dataLoaded, filteredEmails, sortConfig, sortData]);

    useEffect(() => {
        if (dataLoaded) {
            setIsFreeAccount?.(isFreeAccount);
        }
    }, [dataLoaded, isFreeAccount, setIsFreeAccount]);

    useEffect(() => {
        if (location.state?.becomeGuestSuccess) {
            message.success(t('contactsMailbox.becomeGuestSuccess') || 'Welcome! Your mailbox is now active.');
            navigate(location.pathname + location.search, { replace: true, state: {} });
        }
    }, [location.state?.becomeGuestSuccess, location.pathname, location.search, navigate, t]);

    useEffect(() => {
        setLockedRecordAccessCode('');
    }, [previewMessage?.access_identifier]);

    useEffect(() => {
        setPreviewDocumentUploadedFiles({});
    }, [previewMessage?.unique_identifier]);

    // Sync previewMessage when inbox data refreshes (realtime: files, comments, documenso status, etc.)
    useEffect(() => {
        if (!previewMessage?.unique_identifier) return;
        const messageData = allFolderData.inbox || [];
        const documentData = allFolderData.inbox_documents || [];
        const flatData = [...messageData, ...documentData].flatMap((item) => {
            if (item.replies?.length) return [item, ...item.replies];
            return [item];
        });
        const updated = flatData.find((item) => item.unique_identifier === previewMessage.unique_identifier);
        if (updated && JSON.stringify(updated) !== JSON.stringify(previewMessage)) {
            setPreviewMessage(updated);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [inboxData]);

    useEffect(() => {
        if (location.pathname === '/mailbox' && dataLoaded && !requiresVerification && !isFreeAccount) {
            setCanBecomeGuest(true);
        } else {
            setCanBecomeGuest(false);
        }
    }, [location.pathname, dataLoaded, requiresVerification, isFreeAccount, setCanBecomeGuest]);

    const hasDispatchedAccessGranted = useRef(false);
    useEffect(() => {
        if (dataLoaded && !requiresVerification && !hasDispatchedAccessGranted.current) {
            hasDispatchedAccessGranted.current = true;
            window.dispatchEvent(new CustomEvent('contacts-mailbox-access-granted'));
        }
    }, [dataLoaded, requiresVerification]);

    useEffect(() => {
        if (
            dataLoaded &&
            !requiresVerification &&
            !isFreeAccount &&
            searchParams.get('becomeGuest') === '1'
        ) {
            navigate('/become-guest', { replace: true });
        }
    }, [dataLoaded, requiresVerification, isFreeAccount, searchParams, navigate]);

    useEffect(() => {
        if (!dataLoaded || requiresVerification || !rid || isFreeAccount) return;
        const allItems = [
            ...(inboxData.threaded_messages || []),
            ...(inboxData.documents || []),
        ];
        const target = allItems[0];
        if (target?.access_link) {
            navigate(target.access_link, { replace: true });
        }
    }, [dataLoaded, requiresVerification, rid, isFreeAccount, inboxData, navigate]);

    useEffect(() => {
        if (dataLoaded && requiresVerification && !rid) {
            message.info(t('contactsMailbox.needLink'));
            navigate('/', { replace: true });
        }
    }, [dataLoaded, requiresVerification, rid, navigate, t]);

    useEffect(() => {
        if (resendCooldown <= 0) return;
        const timer = setTimeout(() => setResendCooldown((c) => c - 1), 1000);
        return () => clearTimeout(timer);
    }, [resendCooldown]);

    useEffect(() => {
        if (lockedRecordResendCooldown <= 0) return;
        const timer = setTimeout(() => setLockedRecordResendCooldown((c) => c - 1), 1000);
        return () => clearTimeout(timer);
    }, [lockedRecordResendCooldown]);

    useEffect(() => {
        localStorage.setItem('contacts_mailbox_list_width', listWidth.toString());
    }, [listWidth]);

    useEffect(() => {
        const handleMouseMove = (e) => {
            if (!isResizingPanels || !containerRef.current) return;
            const containerRect = containerRef.current.getBoundingClientRect();
            const containerWidth = containerRect.width;
            const mouseX = e.clientX - containerRect.left;
            let newListWidth = (mouseX / containerWidth) * 100;
            newListWidth = Math.max(30, Math.min(80, newListWidth));
            setListWidth(newListWidth);
        };
        const handleMouseUp = () => {
            if (isResizingPanels) {
                setIsResizingPanels(false);
                document.body.classList.remove('contacts-mailbox-resizing-panels');
            }
        };
        if (isResizingPanels) {
            document.addEventListener('mousemove', handleMouseMove);
            document.addEventListener('mouseup', handleMouseUp);
            document.body.classList.add('contacts-mailbox-resizing-panels');
        }
        return () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
            document.body.classList.remove('contacts-mailbox-resizing-panels');
        };
    }, [isResizingPanels]);

    useEffect(() => {
        const handleMouseMove = (e) => {
            if (!isResizingPreview || !previewContentRef.current) return;
            const containerRect = previewContentRef.current.getBoundingClientRect();
            const containerWidth = containerRect.width;
            const mouseX = e.clientX - containerRect.left;
            const mousePercent = (mouseX / containerWidth) * 100;
            const bodyWidth = Math.max(30, Math.min(80, mousePercent));
            const documentsWidth = 100 - bodyWidth;
            const newWidths = { body: bodyWidth, documents: documentsWidth };
            setPreviewColumnWidths(newWidths);
            localStorage.setItem('contacts_mailbox_preview_column_widths', JSON.stringify(newWidths));
        };
        const handleMouseUp = () => {
            if (isResizingPreview) {
                setIsResizingPreview(false);
                document.body.classList.remove('contacts-mailbox-preview-resizing');
            }
        };
        if (isResizingPreview) {
            document.addEventListener('mousemove', handleMouseMove);
            document.addEventListener('mouseup', handleMouseUp);
            document.body.classList.add('contacts-mailbox-preview-resizing');
        }
        return () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
            document.body.classList.remove('contacts-mailbox-preview-resizing');
        };
    }, [isResizingPreview]);

    useEffect(() => {
        const handleMouseMove = (e) => {
            if (!isResizingThread || !previewContentRef.current) return;
            const containerRect = previewContentRef.current.getBoundingClientRect();
            const containerWidth = containerRect.width;
            const mouseX = e.clientX - containerRect.left;
            const threadWidth = containerWidth - mouseX;
            let newPercent = (threadWidth / containerWidth) * 100;
            newPercent = Math.max(15, Math.min(45, newPercent));
            setThreadWidthPercent(newPercent);
            localStorage.setItem('contacts_mailbox_thread_width_percent', newPercent.toString());
        };
        const handleMouseUp = () => {
            if (isResizingThread) {
                setIsResizingThread(false);
                document.body.classList.remove('contacts-mailbox-thread-resizing');
            }
        };
        if (isResizingThread) {
            document.addEventListener('mousemove', handleMouseMove);
            document.addEventListener('mouseup', handleMouseUp);
            document.body.classList.add('contacts-mailbox-thread-resizing');
        }
        return () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
            document.body.classList.remove('contacts-mailbox-thread-resizing');
        };
    }, [isResizingThread]);

    const RESEND_COOLDOWN_SECONDS = 30;

    const handleVerifyAccessCode = async (e) => {
        e?.preventDefault();
        if (!rid || !accessCode.trim()) {
            setVerifyError(t('contactsMailbox.enterCode'));
            return;
        }
        setVerifyError('');
        setVerifying(true);
        setCodeExpired(false);
        try {
            await axios.post('/api/contacts-mailbox/verify/', { rid, access_code: accessCode.trim() });
            message.success(t('contactsMailbox.verified'));
            await fetchInbox();
        } catch (err) {
            if (err.response?.status === 410 && err.response?.data?.expired) {
                setCodeExpired(true);
                setAccessCode('');
                setTimeout(() => codeInputRef.current?.focus(), 50);
            }
            setVerifyError(err.response?.data?.error || t('contactsMailbox.verifyFailed'));
        } finally {
            setVerifying(false);
        }
    };

    const handleResendCode = async (onSuccess) => {
        if (!rid || resendCooldown > 0) return;
        setResending(true);
        setSendError('');
        try {
            await axios.post('/api/contacts-mailbox/resend/', { rid });
            message.success(t('contactsMailbox.codeResent'));
            setCodeExpired(false);
            setVerifyError('');
            setResendCooldown(RESEND_COOLDOWN_SECONDS);
            onSuccess?.();
        } catch (err) {
            const errMsg = err.response?.data?.error || t('contactsMailbox.resendFailed');
            setSendError(errMsg);
        } finally {
            setResending(false);
        }
    };

    const handleResendCodeForRecord = async (recordRid, e) => {
        e?.stopPropagation();
        if (!recordRid || lockedRecordResendCooldown > 0) return;
        setResendingRecordRid(recordRid);
        try {
            await axios.post('/api/contacts-mailbox/resend/', { rid: recordRid });
            message.success(t('contactsMailbox.codeResent'));
            setLockedRecordResendCooldown(RESEND_COOLDOWN_SECONDS);
            setLockedRecordVerifyError('');
            await fetchInbox();
        } catch (err) {
            message.error(err.response?.data?.error || t('contactsMailbox.resendFailed'));
        } finally {
            setResendingRecordRid(null);
        }
    };

    const handleVerifyLockedRecord = async (recordRid) => {
        if (!recordRid || !lockedRecordAccessCode.trim()) {
            setLockedRecordVerifyError(t('contactsMailbox.enterCode'));
            return;
        }
        setLockedRecordVerifyError('');
        setVerifyingLockedRecord(true);
        try {
            await axios.post('/api/contacts-mailbox/verify/', {
                rid: recordRid,
                access_code: lockedRecordAccessCode.trim(),
            });
            message.success(t('contactsMailbox.verified'));
            setLockedRecordAccessCode('');
            await fetchInbox();
        } catch (err) {
            if (err.response?.status === 410 && err.response?.data?.expired) {
                setLockedRecordAccessCode('');
            }
            setLockedRecordVerifyError(err.response?.data?.error || t('contactsMailbox.verifyFailed'));
        } finally {
            setVerifyingLockedRecord(false);
        }
    };

    const handleMessageClick = (messageItem) => {
        if (!messageItem?.access_link) {
            message.error(t('mailbox.unableToAccess'));
            return;
        }
        navigate(messageItem.access_link, { state: { preserveViews: false } });
    };

    const handlePreviewDownload = async (attachmentId, fileName, isDocumenso = false, linkId = null) => {
        try {
            const url = isDocumenso
                ? (linkId ? `/api/docuso/link/${linkId}/download/${attachmentId}/` : `/api/docuso/download/${attachmentId}/`)
                : `/api/messages/download/${attachmentId}/`;
            const res = await axios.get(url, { responseType: 'blob' });
            const blobUrl = window.URL.createObjectURL(new Blob([res.data]));
            const link = document.createElement('a');
            link.href = blobUrl;
            link.setAttribute('download', fileName || 'download');
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(blobUrl);
        } catch {
            message.error(t('messages.view.errorFailedDownload'));
        }
    };

    const handlePreviewDocumentDownload = async (documentFileId, fileName) => {
        try {
            const res = await axios.get(`/api/documents/download/${documentFileId}/`, { responseType: 'blob' });
            const blobUrl = window.URL.createObjectURL(new Blob([res.data]));
            const link = document.createElement('a');
            link.href = blobUrl;
            link.setAttribute('download', fileName || 'document');
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(blobUrl);
        } catch {
            message.error(t('messages.view.errorFailedDownload'));
        }
    };

    useEffect(() => {
        if (previewMessage?.item_type === 'document' && previewMessage.sender !== receiverEmail) {
            axios.get('/api/system-settings/').then(res => {
                setPreviewSystemSettings(res.data);
            }).catch(() => {});
        } else {
            setPreviewSystemSettings(null);
        }
    }, [previewMessage?.item_type, previewMessage?.sender, receiverEmail]);

    const handlePreviewUploadDocument = (file, docFile) => {
        if (docFile.document_status === 'approved' || (docFile.document_name && docFile.document_status === 'pending')) {
            message.warning(docFile.document_status === 'pending'
                ? t('documents.view.pendingCannotModify')
                : t('documents.view.approvedCannotModify'));
            return false;
        }
        if (docFile.return_type !== '*') {
            const allowedTypes = docFile.return_type.split(',').map(ext => ext.trim().toLowerCase());
            const fileExtension = file.name.split('.').pop().toLowerCase();
            const fileType = file.type?.toLowerCase() || '';
            const isValid = allowedTypes.some(type =>
                type === fileExtension || type === fileType || type.includes(fileExtension)
            );
            if (!isValid) {
                message.error(t('documents.view.invalidFileType', { expected: docFile.return_type, received: fileExtension }));
                return false;
            }
        }
        if (previewSystemSettings?.max_upload_size) {
            const maxBytes = parseInt(previewSystemSettings.max_upload_size) * 1024 * 1024;
            if (file.size > maxBytes) {
                message.error(t('documents.view.fileTooLarge', { maxSize: previewSystemSettings.max_upload_size }));
                return false;
            }
        }
        setPreviewDocumentUploadedFiles(prev => ({
            ...prev,
            [docFile.unique_identifier]: { file, name: file.name, type: file.type, size: file.size }
        }));
        message.success(t('documents.view.fileSelectedSuccessfully'));
        return false;
    };

    const handlePreviewRemoveFile = (documentFileId) => {
        setPreviewDocumentUploadedFiles(prev => {
            const next = { ...prev };
            delete next[documentFileId];
            return next;
        });
        message.success(t('documents.view.fileRemovedSuccessfully'));
    };

    const handlePreviewSendDocuments = async () => {
        const entries = Object.entries(previewDocumentUploadedFiles);
        if (entries.length === 0) {
            message.warning(t('documents.view.noFilesToSend'));
            return;
        }
        if (!previewMessage?.unique_identifier) {
            message.error(t('documents.view.errorDocumentNotLoaded'));
            return;
        }
        try {
            const formData = new FormData();
            entries.forEach(([docFileId, data]) => formData.append(`file_${docFileId}`, data.file));
            await axios.post(`/api/save_requested_documents/${previewMessage.unique_identifier}/`, formData);
            message.success(t('documents.view.documentsSent'));
            setPreviewDocumentUploadedFiles({});
            refresh();
        } catch (err) {
            message.error(err.response?.data?.error || t('documents.view.errorSendingDocuments'));
        }
    };

    const handlePreviewApproveDocument = async (documentFileId) => {
        try {
            await axios.post(`/api/approve_deny_document_file/${documentFileId}/`, { action: 'approved' });
            message.success(t('documentPreview.documentApproved'));
            refresh();
        } catch (err) {
            message.error(err.response?.data?.error || t('documentPreview.errors.approveFailed'));
        }
    };

    const handlePreviewDenyDocument = (documentFileId) => {
        setPreviewDenyingFileId(documentFileId);
        setPreviewDenyReason('');
        setPreviewDenyModalVisible(true);
    };

    const handlePreviewConfirmDeny = async () => {
        if (!previewDenyReason.trim()) {
            message.error(t('documentPreview.denyReasonRequired'));
            return;
        }
        try {
            await axios.post(`/api/approve_deny_document_file/${previewDenyingFileId}/`, {
                action: 'denied',
                deny_reason: previewDenyReason
            });
            message.success(t('documentPreview.documentDenied'));
            setPreviewDenyModalVisible(false);
            setPreviewDenyReason('');
            setPreviewDenyingFileId(null);
            refresh();
        } catch (err) {
            message.error(err.response?.data?.error || t('documentPreview.errors.denyFailed'));
        }
    };

    const handlePreviewCancelDeny = () => {
        setPreviewDenyModalVisible(false);
        setPreviewDenyReason('');
        setPreviewDenyingFileId(null);
    };

    const handlePreviewDownloadAll = async () => {
        if (!previewMessage) return;
        try {
            let url;
            if (previewMessage.item_type === 'document') {
                url = `/api/documents/${previewMessage.unique_identifier}/download-all/`;
            } else if (previewMessage.message_type === 'documenso' || previewMessage.documenso_files?.length > 0) {
                url = previewMessage.access_identifier
                    ? `/api/docuso/link/${previewMessage.access_identifier}/download-all/`
                    : `/api/docuso/${previewMessage.unique_identifier}/download-all/`;
            } else {
                url = `/api/messages/${previewMessage.unique_identifier}/download-all/`;
            }
            const res = await axios.get(url, { responseType: 'blob' });
            const blobUrl = window.URL.createObjectURL(new Blob([res.data]));
            const link = document.createElement('a');
            link.href = blobUrl;
            link.setAttribute('download', previewMessage.item_type === 'document' ? 'documents.zip' : 'attachments.zip');
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(blobUrl);
        } catch {
            message.error(t('messages.view.errorFailedDownload'));
        }
    };

    const getReplyIdentifier = (msg) => {
        if (!msg) return null;
        if (msg.access_link) {
            const match = msg.access_link.match(/\/([a-f0-9-]{36})\/?$/i);
            if (match) return match[1];
        }
        return msg.access_identifier || msg.unique_identifier;
    };

    const handleReply = (msg) => {
        const id = getReplyIdentifier(msg);
        if (!id) return;
        const base = msg?.message_type === 'documenso' ? '/documenso/link/reply' : '/link/reply';
        navigate(`${base}/${id}?action=reply`, { state: { preserveViews: true } });
    };

    const handleReplyAll = (msg) => {
        const id = getReplyIdentifier(msg);
        if (!id) return;
        const base = msg?.message_type === 'documenso' ? '/documenso/link/reply' : '/link/reply';
        navigate(`${base}/${id}?reply_all=true&action=reply_all`, { state: { preserveViews: true } });
    };

    const hasMultipleRecipients = (recipients) => {
        if (!recipients) return false;
        if (typeof recipients === 'string') {
            const list = recipients.split(/[,;\s]+/).map((e) => e.trim()).filter(Boolean);
            return list.length > 1;
        }
        return Array.isArray(recipients) && recipients.length > 1;
    };

    const isViewerOnlyInBcc = (msg) => {
        if (!msg || !receiverEmail) return false;
        const bccList = msg.bcc ? msg.bcc.split(/[,;\s]+/).map((e) => e.trim().toLowerCase()) : [];
        const isInBcc = bccList.includes(receiverEmail.toLowerCase());
        const recipientList = msg.recipient ? msg.recipient.split(/[,;\s]+/).map((e) => e.trim().toLowerCase()) : [];
        const ccList = msg.cc ? msg.cc.split(/[,;\s]+/).map((e) => e.trim().toLowerCase()) : [];
        const isInRecipientOrCc =
            recipientList.includes(receiverEmail.toLowerCase()) || ccList.includes(receiverEmail.toLowerCase());
        return isInBcc && !isInRecipientOrCc;
    };

    if (requiresVerification) {
        return (
            <Layout className="contacts-mailbox-layout">
                <Content className="contacts-mailbox-content">
                    <div className="contacts-mailbox-top-bar">
                        <div className="contacts-mailbox-top-folders">
                            <h2 style={{ margin: 0 }}>{t('contactsMailbox.title')}</h2>
                        </div>
                    </div>
                    <div
                        style={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            minHeight: '50vh',
                            padding: 24,
                        }}
                    >
                        {rid ? (
                            <>
                                <LockOutlined style={{ fontSize: 48, color: '#1890ff', marginBottom: 16 }} />
                                {!showAccessCodeInput ? (
                                    <>
                                        <h3 style={{ marginBottom: 8 }}>{t('contactsMailbox.accessYourMailbox')}</h3>
                                        <p style={{ color: '#666', marginBottom: 4 }}>
                                            {t('contactsMailbox.sendCodeHint')}
                                        </p>
                                        <p style={{ color: '#999', fontSize: 13, marginBottom: 24 }}>
                                            {t('contactsMailbox.codeFormatHint')}
                                        </p>
                                        {sendError && (
                                            <p style={{ color: '#ff4d4f', marginBottom: 16, textAlign: 'center', maxWidth: 320 }}>
                                                {sendError}
                                            </p>
                                        )}
                                        <Button
                                            type="primary"
                                            size="large"
                                            loading={resending}
                                            disabled={resendCooldown > 0}
                                            onClick={() => handleResendCode(() => setShowAccessCodeInput(true))}
                                        >
                                            {resendCooldown > 0
                                                ? t('contactsMailbox.resendIn', { seconds: resendCooldown })
                                                : t('contactsMailbox.sendAccessCode')}
                                        </Button>
                                    </>
                                ) : (
                                    <>
                                        <h3 style={{ marginBottom: 8 }}>{t('contactsMailbox.enterAccessCode')}</h3>
                                        <p style={{ color: '#666', marginBottom: 24 }}>
                                            {t('contactsMailbox.accessCodeHint')}
                                        </p>
                                        <form
                                            onSubmit={handleVerifyAccessCode}
                                            style={{ display: 'flex', flexDirection: 'column', gap: 12, maxWidth: 320, width: '100%' }}
                                        >
                                            <Input
                                                ref={codeInputRef}
                                                placeholder={t('contactsMailbox.accessCode')}
                                                value={accessCode}
                                                onChange={(e) => {
                                                    setAccessCode(e.target.value.replace(/\D/g, '').slice(0, 6));
                                                    setVerifyError('');
                                                }}
                                                maxLength={6}
                                                size="large"
                                                disabled={verifying}
                                                autoFocus
                                                status={verifyError ? 'error' : ''}
                                            />
                                            {verifyError && (
                                                <div style={{ color: '#ff4d4f', fontSize: 13, marginTop: -4 }}>
                                                    {verifyError}
                                                </div>
                                            )}
                                            <Button type="primary" htmlType="submit" loading={verifying} size="large" block>
                                                {t('contactsMailbox.verify')}
                                            </Button>
                                            <Button
                                                type="link"
                                                onClick={() => handleResendCode(() => {
                                                    setAccessCode('');
                                                    setTimeout(() => codeInputRef.current?.focus(), 50);
                                                })}
                                                loading={resending}
                                                disabled={resendCooldown > 0}
                                                style={{ marginTop: 4 }}
                                            >
                                                {resendCooldown > 0
                                                    ? t('contactsMailbox.resendIn', { seconds: resendCooldown })
                                                    : t('contactsMailbox.resendCode')}
                                            </Button>
                                        </form>
                                    </>
                                )}
                            </>
                        ) : (
                            <>
                                <MailOutlined style={{ fontSize: 48, color: '#d9d9d9', marginBottom: 16 }} />
                                <h3>{t('contactsMailbox.needLink')}</h3>
                                <p style={{ color: '#666' }}>
                                    {t('contactsMailbox.checkEmail')}
                                </p>
                            </>
                        )}
                    </div>
                </Content>
            </Layout>
        );
    }

    const sortedEmails = sortData(filteredEmails);

    const renderMailContent = () => {
        if (loading) {
            return (
                <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}>
                    <Spin size="large" />
                </div>
            );
        }
        if (filteredEmails.length === 0 && searchText) {
            return <div className="contacts-mailbox-empty">{t('mailbox.noMatchingMessages')}</div>;
        }
        if (filteredEmails.length === 0) {
            return <div className="contacts-mailbox-empty">{t('mailbox.noMessages')}</div>;
        }
        return (
            <div className="contacts-mailbox-list">
                {sortedEmails.map((item) => {
                    const messageItem = item;
                    const hasRepliesFlag = hasReplies(item);
                    const isLocked = !isFreeAccount && messageItem.access_code_valid === false;
                    const isThreadPreviewed =
                        previewMessage &&
                        (previewMessage.unique_identifier === messageItem.unique_identifier ||
                            (hasRepliesFlag && item.replies?.some((r) => r.unique_identifier === previewMessage.unique_identifier)));

                    return (
                        <div key={messageItem.unique_identifier}>
                            <div
                                className={`contacts-mailbox-card ${isThreadPreviewed ? 'contacts-mailbox-card-preview-selected' : ''} ${isLocked ? 'contacts-mailbox-card-locked' : ''}`}
                                data-item-type={messageItem.item_type || 'message'}
                                onClick={() => {
                                    setPreviewMessage(messageItem);
                                    setPreviewThread(hasRepliesFlag ? item : null);
                                }}
                                onDoubleClick={() => !isLocked && handleMessageClick(messageItem)}
                            >
                                <div className="contacts-mailbox-card-checkbox-icon">
                                    <div className="contacts-mailbox-card-icon">
                                        <div className={`contacts-mailbox-icon-wrapper ${!messageItem.has_been_read && !isLocked ? 'unread' : ''}`}>
                                            {isLocked ? (
                                                <LockOutlined />
                                            ) : messageItem.item_type === 'document' ? (
                                                <FileTextOutlined />
                                            ) : messageItem.message_type === 'documenso' ? (
                                                <FontAwesomeIcon icon={faSignature} />
                                            ) : (
                                                <MailOutlined />
                                            )}
                                        </div>
                                    </div>
                                </div>
                                <div className="contacts-mailbox-card-content">
                                    <div className="contacts-mailbox-card-main">
                                        <div className="contacts-mailbox-card-left">
                                            <div className="contacts-mailbox-card-sender">{messageItem.sender}</div>
                                            {isLocked ? (
                                                <div className="contacts-mailbox-card-locked-spacer" aria-hidden="true" />
                                            ) : (
                                                <>
                                                    <div className="contacts-mailbox-card-subject">{messageItem.subject}</div>
                                                    <div className="contacts-mailbox-card-preview">
                                                        {stripHtmlTags(messageItem.body || messageItem.preview || '')}
                                                    </div>
                                                </>
                                            )}
                                        </div>
                                        <div className="contacts-mailbox-card-right">
                                            <div className="contacts-mailbox-card-dates">
                                                {messageItem.created_at ? dayjs(messageItem.created_at).format('YYYY-MM-DD HH:mm') : '-'}
                                                {messageItem.ttlive && <span className="contacts-mailbox-card-date-separator"> / </span>}
                                                {messageItem.ttlive && dayjs(messageItem.ttlive).format('YYYY-MM-DD HH:mm')}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                {!isLocked && messageItem.item_type === 'document' && messageItem.document_files?.length > 0 && (
                                    <div className="contacts-mailbox-card-doc-count">
                                        {messageItem.document_files.length}{' '}
                                        {messageItem.document_files.length === 1 ? t('mailbox.document') : t('mailbox.documents')}
                                    </div>
                                )}
                                {!isLocked && (messageItem.files?.length > 0 || messageItem.documenso_files?.length > 0) && (
                                    <div className="contacts-mailbox-card-doc-count">
                                        {(messageItem.files?.length || 0) + (messageItem.documenso_files?.length || 0)}{' '}
                                        {t('mailbox.files')}
                                    </div>
                                )}
                                {isLocked && messageItem.access_identifier && (
                                    <Button
                                        type="link"
                                        size="small"
                                        className="contacts-mailbox-card-locked-resend-btn"
                                        loading={resendingRecordRid === messageItem.access_identifier}
                                        disabled={lockedRecordResendCooldown > 0}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleResendCodeForRecord(messageItem.access_identifier, e);
                                        }}
                                    >
                                        {lockedRecordResendCooldown > 0
                                            ? t('contactsMailbox.resendIn', { seconds: lockedRecordResendCooldown })
                                            : t('contactsMailbox.requestNewCode')}
                                    </Button>
                                )}
                            </div>
                        </div>
                    );
                })}
                {sortedEmails.length > 0 && (
                    <>
                        <div className="contacts-mailbox-list-spacer" aria-hidden="true" />
                        <div className="contacts-mailbox-list-spacer" aria-hidden="true" />
                    </>
                )}
            </div>
        );
    };

    const renderPreviewPanel = () => {
        if (!previewMessage) {
            return (
                <div className="contacts-mailbox-preview-empty">
                    <MailOutlined style={{ fontSize: 64, color: '#d9d9d9', marginBottom: 16 }} />
                    <h3>{t('mailbox.noMessageSelected')}</h3>
                    <p>{t('mailbox.selectMessageToView')}</p>
                </div>
            );
        }

        const isPreviewLocked = !isFreeAccount && previewMessage.access_code_valid === false;

        if (isPreviewLocked) {
            return (
                <div className="contacts-mailbox-preview-content contacts-mailbox-preview-locked">
                    <div
                        style={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            padding: 48,
                            textAlign: 'center',
                            maxWidth: 400,
                            margin: '0 auto',
                        }}
                    >
                        <LockOutlined style={{ fontSize: 64, color: '#1890ff', marginBottom: 24 }} />
                        <h3>{t('contactsMailbox.accessRequired')}</h3>
                        <p style={{ color: '#666', marginBottom: 24 }}>
                            {t('contactsMailbox.accessRequiredHint')}
                        </p>
                        {previewMessage.access_identifier && (
                            <>
                                <form
                                    onSubmit={(e) => {
                                        e.preventDefault();
                                        handleVerifyLockedRecord(previewMessage.access_identifier);
                                    }}
                                    style={{ display: 'flex', flexDirection: 'column', gap: 12, width: '100%', marginBottom: 8 }}
                                >
                                    <Input
                                        placeholder={t('contactsMailbox.accessCode')}
                                        value={lockedRecordAccessCode}
                                        onChange={(e) => {
                                            setLockedRecordAccessCode(e.target.value.replace(/\D/g, '').slice(0, 6));
                                            setLockedRecordVerifyError('');
                                        }}
                                        maxLength={6}
                                        size="large"
                                        disabled={verifyingLockedRecord}
                                        status={lockedRecordVerifyError ? 'error' : ''}
                                    />
                                    {lockedRecordVerifyError && (
                                        <div style={{ color: '#ff4d4f', fontSize: 13, marginTop: -4, textAlign: 'left' }}>
                                            {lockedRecordVerifyError}
                                        </div>
                                    )}
                                    <Button
                                        type="primary"
                                        htmlType="submit"
                                        loading={verifyingLockedRecord}
                                        size="large"
                                        block
                                    >
                                        {t('contactsMailbox.verify')}
                                    </Button>
                                </form>
                                <Button
                                    type="link"
                                    loading={resendingRecordRid === previewMessage.access_identifier}
                                    disabled={lockedRecordResendCooldown > 0}
                                    onClick={() => handleResendCodeForRecord(previewMessage.access_identifier)}
                                >
                                    {lockedRecordResendCooldown > 0
                                        ? t('contactsMailbox.resendIn', { seconds: lockedRecordResendCooldown })
                                        : t('contactsMailbox.requestNewCode')}
                                </Button>
                            </>
                        )}
                    </div>
                </div>
            );
        }

        const threadReplies = previewThread?.replies || [];
        const hasThreadReplies = threadReplies.length > 0;
        const allThreadMessages = hasThreadReplies ? [previewThread, ...threadReplies] : [];
        const threadAttachments = hasThreadReplies ? (() => {
            const items = [];
            allThreadMessages.forEach((msg) => {
                if (msg.item_type === 'document' && msg.document_files) {
                    (msg.document_files || []).filter((f) => f.document_name).forEach((doc) => {
                        items.push({ ...doc, source: 'document', displayName: doc.document_name });
                    });
                }
                if (msg.message_type === 'documenso' && msg.documenso_files) {
                    (msg.documenso_files || []).forEach((f) => {
                        items.push({ ...f, source: 'documenso', displayName: f.file_name || f.pdf_name, linkId: msg.access_identifier });
                    });
                }
                if (msg.message_type !== 'documenso' && msg.item_type !== 'document' && msg.files) {
                    (msg.files || []).forEach((f) => {
                        items.push({ ...f, source: 'messages', displayName: f.file_name || f.pdf_name });
                    });
                }
            });
            return items;
        })() : [];
        const hasDocumentFiles = previewMessage.item_type === 'document' && (previewMessage.document_files?.length ?? 0) > 0;
        const hasMessageAttachments = (previewMessage.message_type === 'documenso' && previewMessage.documenso_files?.length > 0) ||
            (previewMessage.message_type !== 'documenso' && previewMessage.files?.length > 0);
        const hasAttachmentsOrDocuments = hasThreadReplies ? false : (hasDocumentFiles || hasMessageAttachments);

        return (
            <div className="contacts-mailbox-preview-content">
                {previewMessage.access_link && (
                    <div className="contacts-mailbox-preview-actions">
                        <div>
                            {previewMessage.message_type === 'documenso' && (
                                <>
                                    {previewMessage.sender !== receiverEmail && (previewMessage.documenso_files || []).some((f) => f.envelope_id) && (
                                        <Button
                                            type="primary"
                                            icon={<FontAwesomeIcon icon={faSignature} />}
                                            onClick={() => setDocumensoSignModalOpen(true)}
                                            className="contacts-mailbox-preview-sign-btn"
                                        >
                                            {t('documenso.signDocument', 'Sign')}
                                        </Button>
                                    )}
                                    <Button
                                        type={previewMessage.sender !== receiverEmail ? 'default' : 'primary'}
                                        onClick={() => handleMessageClick(previewMessage)}
                                    >
                                        {t('common.buttons.open')}
                                    </Button>
                                </>
                            )}
                            {previewMessage.message_type !== 'documenso' && (
                                <Button type="primary" onClick={() => handleMessageClick(previewMessage)}>
                                    {t('common.buttons.open')}
                                </Button>
                            )}
                        </div>
                        <div>
                            {((previewMessage.files?.length > 0) ||
                                (previewMessage.documenso_files?.length > 0) ||
                                (previewMessage.item_type === 'document' &&
                                    previewMessage.document_files?.filter((f) => f.document_name).length > 0)) && (
                                <Button icon={<FolderOpenOutlined />} onClick={handlePreviewDownloadAll} />
                            )}
                            {previewMessage.item_type === 'document' && previewMessage.sender !== receiverEmail && Object.keys(previewDocumentUploadedFiles).length > 0 && (
                                <Button
                                    type="primary"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handlePreviewSendDocuments();
                                    }}
                                >
                                    {t('documents.view.sendDocuments')}
                                </Button>
                            )}
                            {previewMessage.item_type !== 'document' && (
                                <>
                                    {(previewMessage?.cc || hasMultipleRecipients(previewMessage?.recipient)) &&
                                        !isViewerOnlyInBcc(previewMessage) && (
                                            <Button onClick={() => handleReplyAll(previewMessage)} className="action-btn">
                                                {t('messages.view.replyAll')}
                                            </Button>
                                        )}
                                    <Button onClick={() => handleReply(previewMessage)} className="action-btn">
                                        {t('messages.view.reply')}
                                    </Button>
                                </>
                            )}
                        </div>
                    </div>
                )}

                <div className="contacts-mailbox-preview-header-row">
                    <div className="contacts-mailbox-preview-header-left">
                        <div className="contacts-mailbox-preview-from">
                            <strong>{t('messages.view.from')}:</strong> {previewMessage.sender || '-'}
                        </div>
                        <div className={`contacts-mailbox-preview-to${!(previewMessage.recipient && String(previewMessage.recipient).trim()) ? ' contacts-mailbox-preview-field-empty' : ''}`}>
                            {(previewMessage.recipient && String(previewMessage.recipient).trim()) ? (
                                <><strong>{t('messages.view.to')}:</strong> {previewMessage.recipient}</>
                            ) : '\u00A0'}
                        </div>
                        <div className={`contacts-mailbox-preview-cc${!(previewMessage.cc && String(previewMessage.cc).trim()) ? ' contacts-mailbox-preview-field-empty' : ''}`}>
                            {(previewMessage.cc && String(previewMessage.cc).trim()) ? (
                                <><strong>{t('messages.view.cc')}:</strong> {previewMessage.cc}</>
                            ) : '\u00A0'}
                        </div>
                        <div className={`contacts-mailbox-preview-bcc${!(previewMessage.bcc && String(previewMessage.bcc).trim()) ? ' contacts-mailbox-preview-field-empty' : ''}`}>
                            {(previewMessage.bcc && String(previewMessage.bcc).trim()) ? (
                                <><strong>{t('messages.view.bcc', 'BCC')}:</strong> {previewMessage.bcc}</>
                            ) : '\u00A0'}
                        </div>
                    </div>
                    <div className="contacts-mailbox-preview-header-right">
                        <div className="contacts-mailbox-preview-created">
                            <span className="contacts-mailbox-preview-date-value">
                                {previewMessage.created_at ? dayjs(previewMessage.created_at).format('MMMM D, YYYY h:mm A') : '-'}
                            </span>
                            <CalendarOutlined className="contacts-mailbox-preview-icon" />
                        </div>
                        <div className="contacts-mailbox-preview-expires">
                            <span className="contacts-mailbox-preview-date-value">
                                {previewMessage.ttlive ? dayjs(previewMessage.ttlive).format('MMMM D, YYYY h:mm A') : '-'}
                            </span>
                            <ClockCircleOutlined className="contacts-mailbox-preview-icon" />
                        </div>
                    </div>
                </div>

                {hasThreadReplies && threadAttachments.length > 0 && (
                    <div className="contacts-mailbox-preview-thread-attachments">
                        {threadAttachments.map((file, index) => {
                            const isDoc = file.source === 'document';
                            const isDocumenso = file.source === 'documenso';
                            const displayName = file.displayName || (isDoc ? file.document_name : (file.file_name || file.pdf_name)) || (t('documensoPreview.documentPending') || 'Document pending');
                            const canPreview = isConvertible(displayName);
                            const commentCount = file.comment_count ?? 0;
                            const onPreview = () => setAttachmentPreviewFile({
                                documentFileId: file.unique_identifier,
                                fileName: displayName,
                                source: file.source,
                                linkId: file.linkId,
                            });
                            const onDownload = () => {
                                if (isDoc) handlePreviewDocumentDownload(file.unique_identifier, displayName);
                                else handlePreviewDownload(file.unique_identifier, displayName, isDocumenso, file.linkId);
                            };
                            return (
                                <div
                                    key={`${file.unique_identifier}-${index}`}
                                    className={`contacts-mailbox-preview-thread-attachment-block ${canPreview ? 'previewable' : ''}`}
                                    onClick={canPreview ? onPreview : undefined}
                                    role={canPreview ? 'button' : undefined}
                                    tabIndex={canPreview ? 0 : undefined}
                                    onKeyDown={canPreview ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onPreview(); } } : undefined}
                                >
                                    <span className="contacts-mailbox-preview-thread-attachment-name" title={displayName}>{displayName}</span>
                                    <span className="contacts-mailbox-preview-thread-attachment-actions">
                                        {canPreview && (
                                            <Button type="text" size="small" icon={<EyeOutlined />} onClick={(e) => { e.stopPropagation(); onPreview(); }} />
                                        )}
                                        <Button type="text" size="small" icon={<DownloadOutlined />} onClick={(e) => { e.stopPropagation(); onDownload(); }} />
                                        {commentCount > 0 && (
                                            <span
                                                className="contacts-mailbox-preview-thread-attachment-comment"
                                                onClick={(e) => { e.stopPropagation(); if (canPreview) { onPreview(); } }}
                                                role={canPreview ? 'button' : undefined}
                                                tabIndex={canPreview ? 0 : undefined}
                                                onKeyDown={canPreview ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onPreview(); } } : undefined}
                                            >
                                                <CommentOutlined />
                                                <span>{commentCount}</span>
                                            </span>
                                        )}
                                    </span>
                                </div>
                            );
                        })}
                    </div>
                )}

                {/* Documenso timeline - sender POV only */}
                {previewMessage.message_type === 'documenso' && previewMessage.sender === receiverEmail && previewMessage.documenso_files?.length > 0 && (
                    <div className="contacts-mailbox-preview-timeline-row">
                        <div className="documenso-preview-timeline documenso-preview-timeline-email mailbox-preview-documenso-timeline">
                            {[
                                { key: 'document_created_at', icon: FileAddOutlined, labelKey: 'documensoPreview.statusCreated' },
                                { key: 'document_sent_at', icon: SendOutlined, labelKey: 'documensoPreview.statusSent' },
                                { key: 'document_opened_at', icon: EyeOutlined, labelKey: 'documensoPreview.statusOpened' },
                                { key: 'document_signed_at', icon: EditOutlined, labelKey: 'documensoPreview.statusSigned' },
                                { key: 'document_completed_at', icon: CheckCircleOutlined, labelKey: 'documensoPreview.statusCompleted' },
                                { key: 'document_rejected_at', icon: CloseCircleOutlined, labelKey: 'documensoPreview.statusRejected' },
                                { key: 'document_cancelled_at', icon: StopOutlined, labelKey: 'documensoPreview.statusCancelled' },
                            ].map(({ key, icon: Icon, labelKey }, i) => {
                                const val = previewMessage[key];
                                const isDone = !!val;
                                const statusType = key === 'document_rejected_at' ? 'rejected' : key === 'document_cancelled_at' ? 'cancelled' : null;
                                return (
                                    <React.Fragment key={key}>
                                        {i > 0 && <span className="documenso-preview-timeline-connector" />}
                                        <span className={`documenso-preview-timeline-step ${isDone ? 'done' : 'pending'} ${statusType ? `status-${statusType}` : ''}`}>
                                            <Icon className="documenso-preview-timeline-icon" />
                                        </span>
                                    </React.Fragment>
                                );
                            })}
                        </div>
                    </div>
                )}

                <div className="contacts-mailbox-preview-subject">{previewMessage.subject ?? ''}</div>

                <div className="contacts-mailbox-preview-content-row" ref={previewContentRef}>
                    <div
                        className="contacts-mailbox-preview-body-column"
                        style={{
                            width: hasAttachmentsOrDocuments ? `${previewColumnWidths.body}%` : (hasThreadReplies ? undefined : '100%'),
                            flex: hasThreadReplies ? 1 : undefined,
                            display: 'flex',
                            flexDirection: 'column',
                            minWidth: 0,
                            minHeight: 0,
                        }}
                    >
                        <div className="contacts-mailbox-preview-body">
                            <div dangerouslySetInnerHTML={{ __html: previewMessage.body || previewMessage.preview || '' }} />
                        </div>
                    </div>
                    {hasAttachmentsOrDocuments && (
                        <div
                            className="contacts-mailbox-preview-resize-handle"
                            onMouseDown={(e) => {
                                e.preventDefault();
                                setIsResizingPreview(true);
                            }}
                        />
                    )}
                    {hasAttachmentsOrDocuments && (
                        <div
                            className="contacts-mailbox-preview-attachments-column"
                            style={{ width: `${previewColumnWidths.documents}%`, minWidth: 200 }}
                        >
                            <div className={previewMessage.item_type === 'document' ? 'mailbox-preview-documents-list-section' : 'mailbox-preview-attachments-column-section'}>
                                <h4 className={previewMessage.item_type === 'document' ? 'mailbox-preview-documents-list-title' : 'mailbox-preview-attachments-column-title'}>
                                    {previewMessage.item_type === 'document'
                                        ? t('documents.view.requestedDocuments', { count: (previewMessage.document_files || []).length }) || `Requested Documents (${(previewMessage.document_files || []).length})`
                                        : t('messages.view.attachments', { count: (previewMessage.message_type === 'documenso' ? (previewMessage.documenso_files || []) : (previewMessage.files || [])).length })}
                                </h4>
                                <div className={previewMessage.item_type === 'document' ? 'mailbox-preview-documents-list' : 'mailbox-preview-attachments-list mailbox-preview-attachments-list-rows'}>
                                    {previewMessage.item_type === 'document' &&
                                        [...(previewMessage.document_files || [])]
                                            .sort((a, b) => (a.document_name || a.title || '').localeCompare(b.document_name || b.title || ''))
                                            .map((docFile, index) => {
                                                const hasUploadedFile = !!docFile.document_name;
                                                const isReceiver = previewMessage.sender !== receiverEmail;
                                                const hasSelectedFile = !!previewDocumentUploadedFiles[docFile.unique_identifier];
                                                const canUpload = isReceiver && docFile.document_status !== 'approved' && (!docFile.document_name || docFile.document_status !== 'pending');
                                                let statusText = '';
                                                let statusColor = '';
                                                if (hasSelectedFile) {
                                                    statusText = t('documents.view.readyToSend') || 'Ready to send';
                                                    statusColor = 'blue';
                                                } else if (!docFile.document_name) {
                                                    statusText = t('documents.view.statusNotSent') || 'NOT SENT';
                                                    statusColor = 'default';
                                                } else {
                                                    switch (docFile.document_status) {
                                                        case 'approved':
                                                            statusText = t('documentPreview.statusApproved') || 'Approved';
                                                            statusColor = 'green';
                                                            break;
                                                        case 'denied':
                                                            statusText = t('documentPreview.statusDenied') || 'Denied';
                                                            statusColor = 'red';
                                                            break;
                                                        case 'pending':
                                                        default:
                                                            statusText = t('documentPreview.statusPending') || 'Pending Review';
                                                            statusColor = 'orange';
                                                            break;
                                                    }
                                                }
                                                const displayFileName = hasSelectedFile
                                                    ? previewDocumentUploadedFiles[docFile.unique_identifier]?.name
                                                    : (hasUploadedFile ? docFile.document_name : null);
                                                const docName = docFile.document_name;
                                                const canPreview = hasUploadedFile && isConvertible(docName);
                                                const commentCount = docFile.comment_count ?? 0;
                                                const rowContent = (
                                                    <>
                                                        {canPreview && commentCount > 0 && (
                                                            <span
                                                                className="mailbox-preview-comment-badge"
                                                                onClick={(e) => { e.stopPropagation(); setAttachmentPreviewFile({ documentFileId: docFile.unique_identifier, fileName: docName, source: 'document' }); }}
                                                                role="button" tabIndex={0}
                                                                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setAttachmentPreviewFile({ documentFileId: docFile.unique_identifier, fileName: docName, source: 'document' }); } }}
                                                            >
                                                                <CommentOutlined />
                                                                <span className="mailbox-preview-comment-badge-count">{commentCount}</span>
                                                            </span>
                                                        )}
                                                        <span className="mailbox-preview-document-title">
                                                            {docFile.title}
                                                            {displayFileName && (
                                                                <span className="mailbox-preview-document-uploaded-name"> : {displayFileName}</span>
                                                            )}
                                                        </span>
                                                        <div className="mailbox-preview-document-right">
                                                        <div className="mailbox-preview-document-status-cell">
                                                            <span className={`mailbox-preview-document-status mailbox-preview-document-status-${statusColor}`}>
                                                                {statusText}
                                                            </span>
                                                        </div>
                                                        <div className="mailbox-preview-document-actions">
                                                            {isReceiver ? (
                                                                <>
                                                                    {canPreview ? (
                                                                        <Button
                                                                            type="text"
                                                                            size="small"
                                                                            icon={<EyeOutlined />}
                                                                            onClick={(e) => { e.stopPropagation(); setAttachmentPreviewFile({ documentFileId: docFile.unique_identifier, fileName: docName, source: 'document' }); }}
                                                                        />
                                                                    ) : (
                                                                        <span className="mailbox-preview-document-action-disabled"><EyeOutlined /></span>
                                                                    )}
                                                                    {canUpload ? (
                                                                        <span className="mailbox-preview-document-upload-icon"><UploadOutlined /></span>
                                                                    ) : (
                                                                        <span className="mailbox-preview-document-action-disabled"><UploadOutlined /></span>
                                                                    )}
                                                                    {hasSelectedFile ? (
                                                                        <Button
                                                                            type="text"
                                                                            size="small"
                                                                            icon={<DeleteOutlined />}
                                                                            onClick={(e) => { e.stopPropagation(); handlePreviewRemoveFile(docFile.unique_identifier); }}
                                                                            className="mailbox-preview-document-remove-btn"
                                                                        />
                                                                    ) : (
                                                                        <span className="mailbox-preview-document-action-disabled"><DeleteOutlined /></span>
                                                                    )}
                                                                </>
                                                            ) : (
                                                                <>
                                                                    {canPreview ? (
                                                                        <Button
                                                                            type="text"
                                                                            size="small"
                                                                            icon={<EyeOutlined />}
                                                                            onClick={(e) => { e.stopPropagation(); setAttachmentPreviewFile({ documentFileId: docFile.unique_identifier, fileName: docName, source: 'document' }); }}
                                                                            className="mailbox-preview-document-preview-btn"
                                                                        />
                                                                    ) : (
                                                                        <span className="mailbox-preview-document-action-disabled"><EyeOutlined /></span>
                                                                    )}
                                                                    {hasUploadedFile ? (
                                                                        <Button
                                                                            type="text"
                                                                            size="small"
                                                                            icon={<DownloadOutlined />}
                                                                            onClick={(e) => { e.stopPropagation(); handlePreviewDocumentDownload(docFile.unique_identifier, docName); }}
                                                                            className="mailbox-preview-document-download-btn"
                                                                        />
                                                                    ) : (
                                                                        <span className="mailbox-preview-document-action-disabled"><DownloadOutlined /></span>
                                                                    )}
                                                                    {hasUploadedFile && docFile.document_status !== 'approved' ? (
                                                                        <Button
                                                                            type="text"
                                                                            size="small"
                                                                            icon={<CheckOutlined />}
                                                                            onClick={(e) => { e.stopPropagation(); handlePreviewApproveDocument(docFile.unique_identifier); }}
                                                                            className="mailbox-preview-document-approve-btn"
                                                                        />
                                                                    ) : (
                                                                        <span className="mailbox-preview-document-action-disabled"><CheckOutlined /></span>
                                                                    )}
                                                                    {hasUploadedFile ? (
                                                                        <Button
                                                                            type="text"
                                                                            size="small"
                                                                            icon={<CloseCircleOutlined />}
                                                                            onClick={(e) => { e.stopPropagation(); handlePreviewDenyDocument(docFile.unique_identifier); }}
                                                                            className={`mailbox-preview-document-deny-btn ${docFile.document_status === 'denied' ? 'denied' : ''}`}
                                                                        />
                                                                    ) : (
                                                                        <span className="mailbox-preview-document-action-disabled"><CloseCircleOutlined /></span>
                                                                    )}
                                                                </>
                                                            )}
                                                        </div>
                                                        </div>
                                                    </>
                                                );
                                                const canPreviewFromRow = hasUploadedFile && !(isReceiver && docFile.document_status === 'denied') && isConvertible(docName);
                                                const handleRowClick = (e) => {
                                                    if (!canPreviewFromRow) return;
                                                    if (e.target.closest('.mailbox-preview-document-actions')) return;
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                    setAttachmentPreviewFile({ documentFileId: docFile.unique_identifier, fileName: docName, source: 'document' });
                                                };
                                                const rowNode = canUpload && !hasSelectedFile ? (
                                                    <Upload
                                                        accept={docFile.return_type === '*' ? '*' : docFile.return_type}
                                                        beforeUpload={(file) => handlePreviewUploadDocument(file, docFile)}
                                                        showUploadList={false}
                                                    >
                                                        <div
                                                            className={`mailbox-preview-document-row mailbox-preview-document-row-clickable ${canPreviewFromRow ? 'mailbox-preview-document-row-previewable' : ''} ${hasUploadedFile && commentCount > 0 ? 'mailbox-preview-document-row-has-comments' : ''}`}
                                                            onClick={handleRowClick}
                                                        >
                                                            {rowContent}
                                                        </div>
                                                    </Upload>
                                                ) : (
                                                    <div
                                                        className={`mailbox-preview-document-row mailbox-preview-document-row-actions ${canPreviewFromRow ? 'mailbox-preview-document-row-previewable' : ''} ${hasUploadedFile && commentCount > 0 ? 'mailbox-preview-document-row-has-comments' : ''}`}
                                                        onClick={canPreviewFromRow ? handleRowClick : undefined}
                                                    >
                                                        {rowContent}
                                                    </div>
                                                );
                                                return (
                                                    <div key={docFile.unique_identifier || index} className="mailbox-preview-document-row-wrapper">
                                                        {rowNode}
                                                    </div>
                                                );
                                            })}
                                    {previewMessage.message_type === 'documenso' &&
                                        (previewMessage.documenso_files || []).map((file, index) => {
                                            const displayName = file.file_name || file.pdf_name;
                                            const hasFile = !!displayName;
                                            const canPreview = hasFile && isConvertible(displayName);
                                            const commentCount = file.comment_count ?? 0;
                                            const onPreview = () => setAttachmentPreviewFile({ documentFileId: file.unique_identifier, fileName: displayName, source: 'documenso', linkId: previewMessage.access_identifier });
                                            const onDownload = () => handlePreviewDownload(file.unique_identifier, displayName, true, previewMessage.access_identifier);
                                            return (
                                                <div
                                                    key={file.unique_identifier || index}
                                                    className={`mailbox-preview-attachment-row ${canPreview ? 'mailbox-preview-attachment-row-previewable' : ''} ${canPreview && commentCount > 0 ? 'mailbox-preview-row-has-comments' : ''}`}
                                                    onClick={canPreview ? onPreview : undefined}
                                                    role={canPreview ? 'button' : undefined}
                                                    tabIndex={canPreview ? 0 : undefined}
                                                    onKeyDown={canPreview ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onPreview(); } } : undefined}
                                                >
                                                    {canPreview && commentCount > 0 && (
                                                        <span className="mailbox-preview-comment-badge"
                                                            onClick={(e) => { e.stopPropagation(); onPreview(); }}
                                                            role="button" tabIndex={0}
                                                            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onPreview(); } }}
                                                        >
                                                            <CommentOutlined />
                                                            <span className="mailbox-preview-comment-badge-count">{commentCount}</span>
                                                        </span>
                                                    )}
                                                    <div className="mailbox-preview-attachment-title-cell">
                                                        <span className="mailbox-preview-attachment-row-title">
                                                            {displayName || (t('documensoPreview.documentPending') || 'Document pending')}
                                                        </span>
                                                    </div>
                                                    <div className="mailbox-preview-attachment-actions">
                                                        {canPreview ? (
                                                            <Button
                                                                type="text"
                                                                size="small"
                                                                icon={<EyeOutlined />}
                                                                className="mailbox-preview-attachment-preview-btn"
                                                                onClick={(e) => { e.stopPropagation(); onPreview(); }}
                                                            />
                                                        ) : (
                                                            <span className="mailbox-preview-document-action-disabled"><EyeOutlined /></span>
                                                        )}
                                                        {hasFile ? (
                                                            <Button
                                                                type="text"
                                                                size="small"
                                                                icon={<DownloadOutlined />}
                                                                className="mailbox-preview-attachment-download-btn"
                                                                onClick={(e) => { e.stopPropagation(); onDownload(); }}
                                                            />
                                                        ) : (
                                                            <span className="mailbox-preview-document-action-disabled"><DownloadOutlined /></span>
                                                        )}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    {previewMessage.message_type !== 'documenso' &&
                                        previewMessage.item_type !== 'document' &&
                                        (previewMessage.files || []).map((file, index) => {
                                            const fileName = file.file_name || file.pdf_name;
                                            const canPreview = isConvertible(fileName);
                                            const commentCount = file.comment_count ?? 0;
                                            const formattedSize = file.file_size && (typeof file.file_size === 'number'
                                                ? file.file_size >= 1024 * 1024
                                                    ? `${(file.file_size / (1024 * 1024)).toFixed(2)} MB`
                                                    : `${(file.file_size / 1024).toFixed(2)} KB`
                                                : file.file_size);
                                            const onPreview = () => setAttachmentPreviewFile({ documentFileId: file.unique_identifier, fileName, source: 'messages' });
                                            const onDownload = () => handlePreviewDownload(file.unique_identifier, fileName, false);
                                            return (
                                                <div
                                                    key={file.unique_identifier || index}
                                                    className={`mailbox-preview-attachment-row ${canPreview ? 'mailbox-preview-attachment-row-previewable' : ''} ${canPreview && commentCount > 0 ? 'mailbox-preview-row-has-comments' : ''}`}
                                                    onClick={canPreview ? onPreview : undefined}
                                                    role={canPreview ? 'button' : undefined}
                                                    tabIndex={canPreview ? 0 : undefined}
                                                    onKeyDown={canPreview ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onPreview(); } } : undefined}
                                                >
                                                    {canPreview && commentCount > 0 && (
                                                        <span className="mailbox-preview-comment-badge"
                                                            onClick={(e) => { e.stopPropagation(); onPreview(); }}
                                                            role="button" tabIndex={0}
                                                            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onPreview(); } }}
                                                        >
                                                            <CommentOutlined />
                                                            <span className="mailbox-preview-comment-badge-count">{commentCount}</span>
                                                        </span>
                                                    )}
                                                    <div className="mailbox-preview-attachment-title-cell">
                                                        <span className="mailbox-preview-attachment-row-title">{fileName}</span>
                                                        {formattedSize && (
                                                            <span className="mailbox-preview-attachment-row-filesize">{formattedSize}</span>
                                                        )}
                                                    </div>
                                                    <div className="mailbox-preview-attachment-actions">
                                                        {canPreview && (
                                                            <Button
                                                                type="text"
                                                                size="small"
                                                                icon={<EyeOutlined />}
                                                                className="mailbox-preview-attachment-preview-btn"
                                                                onClick={(e) => { e.stopPropagation(); onPreview(); }}
                                                            />
                                                        )}
                                                        <Button
                                                            type="text"
                                                            size="small"
                                                            icon={<DownloadOutlined />}
                                                            className="mailbox-preview-attachment-download-btn"
                                                            onClick={(e) => { e.stopPropagation(); onDownload(); }}
                                                        />
                                                    </div>
                                                </div>
                                            );
                                        })}
                                </div>
                            </div>
                        </div>
                    )}
                    {hasThreadReplies && previewThread && (() => {
                        const allThreadMessages = [previewThread, ...threadReplies];
                        const totalCount = allThreadMessages.length;
                        return (
                            <>
                            <div
                                className="contacts-mailbox-preview-thread-resize-handle"
                                onMouseDown={(e) => {
                                    e.preventDefault();
                                    setIsResizingThread(true);
                                }}
                            />
                            <div
                                className="contacts-mailbox-preview-replies"
                                style={{ flex: `0 0 ${threadWidthPercent}%`, minWidth: 180 }}
                            >
                                <div className="contacts-mailbox-preview-replies-header">
                                    {t('mailbox.thread')} ({totalCount})
                                </div>
                                <div className="contacts-mailbox-preview-replies-list">
                                    {allThreadMessages.map((msg, index) => (
                                        <div
                                            key={msg.unique_identifier}
                                            className={`contacts-mailbox-preview-reply-item ${previewMessage.unique_identifier === msg.unique_identifier ? 'active' : ''} ${index === 0 ? 'contacts-mailbox-preview-reply-original' : ''}`}
                                            onClick={() => setPreviewMessage(msg)}
                                            role="button"
                                            tabIndex={0}
                                            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setPreviewMessage(msg); } }}
                                            aria-label={`${msg.sender || ''} - ${msg.subject || ''}`}
                                        >
                                            <div className="contacts-mailbox-preview-reply-sender">{msg.sender || '-'}</div>
                                            <div className="contacts-mailbox-preview-reply-subject">{msg.subject ?? ''}</div>
                                            <div className="contacts-mailbox-preview-reply-date">
                                                {msg.created_at ? dayjs(msg.created_at).format('MMM D, h:mm A') : '-'}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                            </>
                        );
                    })()}
                </div>
            </div>
        );
    };

    const messagesCount = excludeDocumenso(allFolderData.inbox || []).length;
    const documentsCount = (allFolderData.inbox_documents || []).length;
    const signatureRequestsCount = countSignatureRequests(allFolderData.inbox || []);
    const isSingleRecordMode = !!rid && !isFreeAccount;

    return (
        <Layout className="contacts-mailbox-layout">
            <Content className="contacts-mailbox-content">
                {!isSingleRecordMode && (
                    <div className="contacts-mailbox-top-bar">
                        <div className="contacts-mailbox-top-folders">
                            <div className="contacts-mailbox-section-toggles">
                                <Button
                                    type="text"
                                    icon={<MailOutlined />}
                                    onClick={() => setSectionView(sectionView === 'messages' ? 'all' : 'messages')}
                                    className={`contacts-mailbox-section-toggle-btn ${sectionView === 'all' || sectionView === 'messages' ? 'active' : ''}`}
                                    title={t('mailbox.messages')}
                                />
                                <Button
                                    type="text"
                                    icon={<FileTextOutlined />}
                                    onClick={() => setSectionView(sectionView === 'documents' ? 'all' : 'documents')}
                                    className={`contacts-mailbox-section-toggle-btn ${sectionView === 'all' || sectionView === 'documents' ? 'active' : ''}`}
                                    title={t('mailbox.documents')}
                                />
                                <Button
                                    type="text"
                                    icon={<InboxOutlined />}
                                    onClick={() => setSectionView(sectionView === 'signature_requests' ? 'all' : 'signature_requests')}
                                    className={`contacts-mailbox-section-toggle-btn ${sectionView === 'all' || sectionView === 'signature_requests' ? 'active' : ''}`}
                                    title={t('mailbox.signatureRequests')}
                                />
                            </div>
                            <div className="contacts-mailbox-folder-buttons">
                                <Button type="primary" className="contacts-mailbox-folder-btn active">
                                    {t('mailbox.inbox')} ({messagesCount + documentsCount})
                                </Button>
                            </div>
                        </div>
                        <div className="contacts-mailbox-top-actions">
                            {receiverEmail && (
                                <span className="contacts-mailbox-user-email" style={{ marginRight: 12 }}>{receiverEmail}</span>
                            )}
                        </div>
                    </div>
                )}

                <div className="contacts-mailbox-main-container" ref={containerRef}>
                    {!isSingleRecordMode && (
                    <div className="contacts-mailbox-list-container" style={{ flex: `0 0 ${listWidth}%` }}>
                        <div className="contacts-mailbox-search-section-wrapper">
                            <div className="contacts-mailbox-search-sort-container">
                                <div className="contacts-mailbox-search">
                                    <Input
                                        placeholder={t('mailbox.search')}
                                        prefix={<SearchOutlined />}
                                        value={searchText}
                                        onChange={(e) => setSearchText(e.target.value)}
                                        allowClear
                                        autoComplete="off"
                                        autoCorrect="off"
                                        autoCapitalize="off"
                                        spellCheck="false"
                                    />
                                </div>
                                <div className="contacts-mailbox-sort-dropdown">
                                    <Dropdown
                                        menu={{
                                            items: [
                                                {
                                                    key: 'created',
                                                    label: (
                                                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                                            <span>
                                                                <CalendarOutlined style={{ marginRight: 8 }} />
                                                                {t('mailbox.receivedAt') || 'Received At'}
                                                            </span>
                                                            {sortConfig.key === 'created' && (sortConfig.direction === 'ascend' ? '↑' : '↓')}
                                                        </div>
                                                    ),
                                                    onClick: () =>
                                                        setSortConfig({
                                                            key: 'created',
                                                            direction:
                                                                sortConfig.key === 'created' && sortConfig.direction === 'ascend' ? 'descend' : 'ascend',
                                                        }),
                                                },
                                                {
                                                    key: 'expires',
                                                    label: (
                                                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                                            <span>
                                                                <ClockCircleOutlined style={{ marginRight: 8 }} />
                                                                {t('mailbox.expiryDate') || 'Expiry Date'}
                                                            </span>
                                                            {sortConfig.key === 'expires' && (sortConfig.direction === 'ascend' ? '↑' : '↓')}
                                                        </div>
                                                    ),
                                                    onClick: () =>
                                                        setSortConfig({
                                                            key: 'expires',
                                                            direction:
                                                                sortConfig.key === 'expires' && sortConfig.direction === 'ascend' ? 'descend' : 'ascend',
                                                        }),
                                                },
                                                {
                                                    key: 'subject',
                                                    label: (
                                                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                                            <span>
                                                                <FileTextOutlined style={{ marginRight: 8 }} />
                                                                {t('mailbox.subject') || 'Subject'}
                                                            </span>
                                                            {sortConfig.key === 'subject' && (sortConfig.direction === 'ascend' ? '↑' : '↓')}
                                                        </div>
                                                    ),
                                                    onClick: () =>
                                                        setSortConfig({
                                                            key: 'subject',
                                                            direction:
                                                                sortConfig.key === 'subject' && sortConfig.direction === 'ascend' ? 'descend' : 'ascend',
                                                        }),
                                                },
                                            ],
                                        }}
                                        trigger={['click']}
                                    >
                                        <Button style={{ width: '100%' }}>
                                            <Space style={{ width: '100%', justifyContent: 'space-between' }}>
                                                <span>
                                                    {sortConfig.key === 'created' ? (
                                                        <>
                                                            {t('mailbox.receivedAt') || 'Received At'}
                                                            {' '}
                                                            {sortConfig.direction === 'ascend' ? '↑' : '↓'}
                                                        </>
                                                    ) : sortConfig.key === 'expires' ? (
                                                        <>
                                                            {t('mailbox.expiryDate') || 'Expiry Date'}
                                                            {' '}
                                                            {sortConfig.direction === 'ascend' ? '↑' : '↓'}
                                                        </>
                                                    ) : sortConfig.key === 'subject' ? (
                                                        <>
                                                            {t('mailbox.subject') || 'Subject'}
                                                            {' '}
                                                            {sortConfig.direction === 'ascend' ? '↑' : '↓'}
                                                        </>
                                                    ) : (
                                                        t('mailbox.sortBy') || 'Sort By'
                                                    )}
                                                </span>
                                                <DownOutlined />
                                            </Space>
                                        </Button>
                                    </Dropdown>
                                </div>
                            </div>
                        </div>
                        <div className="contacts-mailbox-list-scrollable">{renderMailContent()}</div>
                    </div>
                    )}
                    {!isSingleRecordMode && (
                    <div
                        className="contacts-mailbox-resize-handle-vertical"
                        onMouseDown={(e) => {
                            e.preventDefault();
                            setIsResizingPanels(true);
                        }}
                    />
                    )}
                    <div
                        className="contacts-mailbox-preview-container"
                        style={{ flex: isSingleRecordMode ? '1 1 100%' : `0 0 ${100 - listWidth}%` }}
                    >
                        {renderPreviewPanel()}
                    </div>
                </div>
            </Content>
            <DocumensoSignModal
                open={documensoSignModalOpen}
                onClose={() => setDocumensoSignModalOpen(false)}
                linkId={previewMessage ? getReplyIdentifier(previewMessage) : null}
                documensoFiles={previewMessage?.documenso_files || []}
                viewerEmail={receiverEmail}
                onSuccess={() => fetchInbox()}
            />

            <AttachmentPreview
                open={!!attachmentPreviewFile}
                onClose={() => setAttachmentPreviewFile(null)}
                documentFileId={attachmentPreviewFile?.documentFileId}
                fileName={attachmentPreviewFile?.fileName}
                source={attachmentPreviewFile?.source || 'document'}
                linkId={attachmentPreviewFile?.linkId}
                onDownload={attachmentPreviewFile ? () => {
                    if (attachmentPreviewFile.source === 'document') {
                        handlePreviewDocumentDownload(attachmentPreviewFile.documentFileId, attachmentPreviewFile.fileName);
                    } else {
                        handlePreviewDownload(attachmentPreviewFile.documentFileId, attachmentPreviewFile.fileName, attachmentPreviewFile.source === 'documenso', attachmentPreviewFile.linkId);
                    }
                } : undefined}
            />
            <Modal
                title={
                    <Space>
                        <CloseCircleOutlined style={{ color: '#ff4d4f' }} />
                        {t('documentPreview.rejectingDocument', { documentName: previewMessage?.document_files?.find(f => f.unique_identifier === previewDenyingFileId)?.title || 'document' })}
                    </Space>
                }
                open={previewDenyModalVisible}
                onOk={handlePreviewConfirmDeny}
                onCancel={handlePreviewCancelDeny}
                okText={t('documentPreview.confirmDeny')}
                cancelText={t('documentPreview.cancel')}
                okButtonProps={{ danger: true }}
                width={600}
            >
                <div style={{ marginBottom: 16 }}>
                    <Text>{t('documentPreview.denyReasonPrompt')}</Text>
                </div>
                <Input.TextArea
                    value={previewDenyReason}
                    onChange={(e) => setPreviewDenyReason(e.target.value)}
                    placeholder={t('documentPreview.denyReasonPlaceholder')}
                    rows={4}
                    maxLength={500}
                />
            </Modal>
        </Layout>
    );
};

export default ContactsMailbox;
